import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import ActionsRenderer from "../editors/renderers/ActionsRenderer";
import { Database, Map } from "lucide-react";
import NexusQueryEditor from "./NexusQueryEditor";

/**
 * NexusGrid - Reusable grid wrapper with a header and default Actions column.
 * Props:
 * - title?: string - optional header title
 * - actionText: string - text for the main action button
 * - onAdd: () => void - handler for the main action button
 * - columnDefs: ColDef[] - user-supplied column definitions (without Actions)
 * - rowData: any[]
 * - defaultColDef?: ColDef
 * - autoGroupColumnDef?: ColDef
 * - gridRef?: React.RefObject<AgGridReact>
 * - onOpenSql?: () => void
 * - onOpenMapping?: () => void
 * - onSqlApply?: (query: string) => void
 * - sqlInitialQuery?: string
 * - sqlLanguage?: string
 * - sqlEditorTitle?: string
 * - showRowNumbers?: boolean - include Row # column as first column
 * - showActions?: boolean - include Actions column (default true)
 * - className?: string - theme class for grid container
 * - containerClassName?: string - outer container classes
 * - style?: React.CSSProperties - style for grid container
 * - ...gridProps - any additional AgGridReact props
 */
const NexusGrid = ({
  title,
  actionText,
  onAdd,
  columnDefs = [],
  rowData,
  defaultColDef,
  autoGroupColumnDef,
  gridRef: externalGridRef,
  onOpenSql,
  onOpenMapping,
  onSqlApply,
  sqlInitialQuery = "",
  sqlLanguage = "sql",
  sqlEditorTitle,
  showRowNumbers = false,
  showActions = true,
  className = "ag-theme-alpine",
  containerClassName = "border border-slate-200 rounded-lg bg-white shadow-sm mb-6",
  style = { height: 480, width: "100%" },
  children,
  ...gridProps
}) => {
  const internalRef = useRef(null);
  const gridRef = externalGridRef || internalRef;
  const [isSqlOpen, setIsSqlOpen] = useState(false);
  const [sqlValue, setSqlValue] = useState("");

  const mergedColumnDefs = useMemo(() => {
    const base = Array.isArray(columnDefs) ? [...columnDefs] : [];

    // Optionally prepend Row # column if not present
    if (showRowNumbers) {
      const hasRowNum = base.some(
        (c) =>
          c.colId === "rowNumber" ||
          c.field === "rowNumber" ||
          c.headerName === "Row #"
      );
      if (!hasRowNum) {
        base.unshift({
          headerName: "Row #",
          colId: "rowNumber",
          valueGetter: (p) => (p.node ? p.node.rowIndex + 1 : 1),
          width: 85,
          minWidth: 85,
          sortable: false,
          filter: false,
          volatile: true,
          cellClass: "flex items-center justify-center",
        });
      }
    }

    // Optionally append Actions column if not present
    if (showActions) {
      const hasActions = base.some(
        (c) => c.field === "actions" || c.colId === "actions"
      );
      if (!hasActions) {
        base.push({
          headerName: "Actions",
          field: "actions",
          width: 100,
          minWidth: 100,
          cellRenderer: ActionsRenderer,
          pinned: "right",
        });
      }
    }

    return base;
  }, [columnDefs, showRowNumbers, showActions]);

  const displayedActionText =
    actionText && actionText.trim() ? actionText : "Add New Row";

  // Provide a sane defaultColDef with vertical centering; allow overrides via prop
  const mergedDefaultColDef = useMemo(() => {
    return { cellClass: "flex items-center", ...(defaultColDef || {}) };
  }, [defaultColDef]);

  const handleOnOpen = () => {
    setSqlValue(sqlInitialQuery || "");
    setIsSqlOpen(true);
    if (onOpenSql) {
      try {
        onOpenSql();
      } catch (_) {}
    }
  };

  const handleOnSqlApply = (q, result) => {
    // remember the applied query so next open shows the modified query
    setSqlValue(q ?? "");

    if (onSqlApply) {
      try {
        onSqlApply(q, result);
      } catch (_) {}
    } else {
      // Default behavior: if results are an array, apply them directly to the grid
      try {
        const api = gridRef.current?.api;
        if (api && result && Array.isArray(result.results)) {
          api.setRowData(result.results);
        }
      } catch (e) {
        // swallow - dev convenience
        // console.error('Failed to apply SQL results to grid', e);
      }
    }

    setIsSqlOpen(false);
  };

  return (
    <div className={containerClassName}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="text-base font-medium text-slate-800">
          {title || ""}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
            onClick={onAdd}
          >
            {displayedActionText}
          </button>
          <button
            type="button"
            title="Open SQL editor"
            aria-label="Open SQL editor"
            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-purple-300 bg-purple-50 text-purple-600 hover:bg-purple-100 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-300"
            onClick={handleOnOpen}
          >
            <Database size={18} />
          </button>
          <button
            type="button"
            title="Open mapping"
            aria-label="Open mapping"
            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            onClick={onOpenMapping}
          >
            <Map size={18} />
          </button>
        </div>
      </div>
      <div className="p-4">
        {children ? (
          children
        ) : (
          <div className={`${className} rounded-lg`} style={style}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={mergedColumnDefs}
              defaultColDef={mergedDefaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              {...gridProps}
            />
          </div>
        )}
      </div>
      <NexusQueryEditor
        isOpen={isSqlOpen}
        initialQuery={sqlValue}
        language={sqlLanguage}
        title={sqlEditorTitle || `${title || ""} SQL Query Editor`}
        onApply={handleOnSqlApply}
        onCancel={() => setIsSqlOpen(false)}
      />
    </div>
  );
};

export default NexusGrid;
